import { createSlice } from '@reduxjs/toolkit'
import { ISLA_SAONA, SANTO_DOMINGO } from '../../util/constants'

const initialRoom = {
  room_number: 1,
  adults: 1,
  children: 0,
  children_ages: [],
};

// Tipos de ruta de traslado disponibles
export const TRANSFER_ROUTES = [
  { key: 'AIRPORT_TO_HOTEL',            label: 'Aeropuerto → Hotel',            code: 'TRF IN' },
  { key: 'HOTEL_TO_AIRPORT',            label: 'Hotel → Aeropuerto',            code: 'TRF OUT' },
  { key: 'AIRPORT_TO_HOTEL_TO_AIRPORT', label: 'Aeropuerto → Hotel → Aeropuerto', code: 'TRF IN/OUT' },
  { key: 'INTER_HOTEL',                 label: 'Inter-hotel',                    code: 'TRF HTL' },
];

export const DEFAULT_ROUTE_ITEMS = TRANSFER_ROUTES.map(r => ({
  route: r.key,
  active: false,
  pricePerPerson: 0,
  originLabel: '',
  destinationLabel: '',
}));

// Helper: calcula el costo total de traslado por persona a partir de los items activos
const deriveTraslado = (trasladoItems = []) =>
  trasladoItems.filter(i => i.active).reduce((sum, i) => sum + Number(i.pricePerPerson || 0), 0);

export const ALL_PREDEFINED_INCLUSIONS = [
  'Boletos aéreos de ida y vuelta (según ruta y aerolínea seleccionada).',
  'Traslados aeropuerto - hotel - aeropuerto en servicio compartido.',
  'Alojamiento en el hotel de su elección según noches programadas.',
  'Régimen de alimentación según el hotel seleccionado.',
  'Soporte y asistencia 24/7.',
  'Tarjeta de asistencia al viajero.'
];

export const DEFAULT_PROGRAM_INCLUSIONS = [
  {
    id: 'flight',
    iconType: 'flight',
    title: 'Boletos aéreos {route}.',
    defaultTitle: 'Boletos aéreos {route}.',
    subtitle: 'clase económica con asientos aleatorios asignados por la aerolínea.',
    defaultSubtitle: 'clase económica con asientos aleatorios asignados por la aerolínea.',
    isDynamic: true,
    dynamicType: 'flight',
    checked: true,
  },
  {
    id: 'transfer',
    iconType: 'transfer',
    title: 'Traslados aeropuerto - hotel - aeropuerto en servicio compartido.',
    defaultTitle: 'Traslados aeropuerto - hotel - aeropuerto en servicio compartido.',
    subtitle: 'Recepción en el aeropuerto de destino por parte del trasladista con un cartel a nombre del titular de la reserva.',
    defaultSubtitle: 'Recepción en el aeropuerto de destino por parte del trasladista con un cartel a nombre del titular de la reserva.',
    isDynamic: false,
    checked: true,
  },
  {
    id: 'hotel',
    iconType: 'hotel',
    title: '{days} días / {nights} noches de alojamiento en el hotel de su elección.',
    defaultTitle: '{days} días / {nights} noches de alojamiento en el hotel de su elección.',
    subtitle: 'Uso de instalaciones y áreas comunes de acuerdo con las políticas de cada hotel.',
    defaultSubtitle: 'Uso de instalaciones y áreas comunes de acuerdo con las políticas de cada hotel.',
    isDynamic: true,
    dynamicType: 'hotel',
    checked: true,
  },
  {
    id: 'meal',
    iconType: 'meal',
    title: 'Régimen de alimentación según el hotel seleccionado.',
    defaultTitle: 'Régimen de alimentación según el hotel seleccionado.',
    subtitle: 'Plan Todo Incluido (All Inclusive: desayunos, almuerzos, cenas, snacks y bebidas alcohólicas y no alcohólicas ilimitadas). Incluye acceso a restaurantes internacionales y de especialidad (sujeto a disponibilidad del hotel).',
    defaultSubtitle: 'Plan Todo Incluido (All Inclusive: desayunos, almuerzos, cenas, snacks y bebidas alcohólicas y no alcohólicas ilimitadas). Incluye acceso a restaurantes internacionales y de especialidad (sujeto a disponibilidad del hotel).',
    isDynamic: true,
    dynamicType: 'meal',
    checked: true,
  },
  {
    id: 'support',
    iconType: 'support',
    title: 'Soporte y asistencia 24/7.',
    defaultTitle: 'Soporte y asistencia 24/7.',
    subtitle: 'Monitoreo continuo para la supervisión y confirmación de todos sus servicios adquiridos.',
    defaultSubtitle: 'Monitoreo continuo para la supervisión y confirmación de todos sus servicios adquiridos.',
    isDynamic: false,
    checked: true,
  },
  {
    id: 'assistance',
    iconType: 'assistance',
    title: 'Tarjeta de asistencia al viajero.',
    defaultTitle: 'Tarjeta de asistencia al viajero.',
    subtitle: 'Cobertura médica y de asistencia en viaje ante imprevistos durante todo el recorrido',
    defaultSubtitle: 'Cobertura médica y de asistencia en viaje ante imprevistos durante todo el recorrido',
    isDynamic: false,
    checked: true,
  },
];

export const ALL_PREDEFINED_EXCLUSIONS = [
  'Gastos personales de cualquier índole.',
  'Servicios extras en el hotel no especificados como spa, masajes, consumo en restaurantes/bares fuera del plan reservado, lavandería, Gimnasio, Otros. (consulta directa del pasajero en la recepción del hotel).',
  'Equipaje adicional o selección de asientos con costo extra.',
  'Propinas voluntarias u obligatorias para el personal de servicio local, según las políticas y la costumbre de cada destino.',
  'Todo lo que no esté expresamente mencionado en la descripción de cada tour (en caso de ser contratados).',
  'Alimentación no detallada en el programa.',
  'Trámites de pasaporte, visados, vacunas o seguro de viaje (salvo que se indique como incluido).',
  'Cualquier servicio o gasto no mencionado expresamente en la sección "El Programa Incluye".'
];

export const ALL_IMPORTANT_NOTES = [
  'Tipo de acomodación: La cantidad y distribución de las camas dentro de la habitación queda estrictamente sujeta a la disponibilidad del hotel al momento del check-in.',
  'Horarios de Check-in y Check-out: Check-in a partir de las 3:00 PM y Check-out hasta las 12:00 PM (sujeto a las políticas de cada hotel).',
  'Las habitaciones son de categoría estándar; esto significa que en el caso de hoteles de playa pueden tener vista al jardín, campo o áreas comunes, y en hoteles de ciudad pueden contar con vista a la calle, vista a la ciudad, vista interna o sin vista. Si el cliente desea garantizar una vista específica (como vista al mar, vista frontal, etc.), deberá consultarlo previamente con su asesor de viajes para verificar el suplemento o tarifa correspondiente.',
  'Garantía en Hotel: Algunos establecimientos pueden solicitar una tarjeta de crédito o depósito en efectivo como garantía, al momento del ingreso.',
  'Orden de los Tours: El orden de las excursiones puede variar según la logística del operador local en destino, condiciones de clima o fuerza mayor, manteniendo la ejecución de los servicios contratados.',
  'Documentación de Viaje: Es responsabilidad exclusiva del pasajero contar con la documentación requerida al día (DNI o pasaporte con vigencia mínima de 6 meses previa al retorno, visados, vacunas y permisos notariales para menores).',
  'Gestión de Cortesías: Las solicitudes de atenciones especiales (cumpleaños, luna de miel, aniversarios, habitaciones contiguas, etc.) se gestionan únicamente como peticiones. La agencia registrará la solicitud en la reserva, pero su confirmación y ejecución quedan totalmente sujetas a la disponibilidad del hotel.'
];

export const IMPORTANT_INFO_ITEMS = [
  'Al contratar o adquirir cualquiera de nuestros servicios turísticos, el cliente declara haber leído, comprendido y aceptado de manera expresa e íntegra los términos y condiciones generales descritos en este documento.',
  'Precios por persona calculados sobre la base de habitación doble o triple estándar (salvo que se indique una acomodación distinta en la propuesta).',
  'Todos los pagos, depósitos o transferencias deberán realizarse exclusivamente a nombre de la empresa Ruta Mundial S.A.C. en las cuentas bancarias corporativas autorizadas.',
  'Las tarifas están sujetas a variación y disponibilidad hasta el momento de realizar el pago total y concretar la emisión de los boletos aéreos y reservas hoteleras.',
  'Las tarifas expresadas aplican únicamente para pagos mediante transferencia bancaria o depósito en efectivo. Para pagos realizados con tarjeta de crédito o débito (vía pasarela o POS), se aplicará un recargo administrativo adicional del 5%.',
  'Los precios cotizados en moneda nacional (soles) están sujetos a variación según el tipo de cambio del día vigente al momento de realizar el pago. Se confirmará el monto exacto antes de procesar la transacción.',
  'De conformidad con la Resolución de Superintendencia Nº 166-2004/SUNAT, el boleto electrónico (e-ticket) emitido directamente por la aerolínea a nombre del pasajero constituye el comprobante de pago oficial del servicio de transporte aéreo.'
];

export const initialState = {
  cotizationDetail: {
    id: null,
    originCity: 'Lima',
    originAirport: 'LIM',
    rooms: [],
    startDate: "",
    endDate: "",
    totalAdults: 1,
    totalChildren: 0,
    childrenAges: [],
    days: 8,
    adultFee: 150,
    kidFee: 120,
    infantFee: 50,
    bookingFeeAdult: 0,
    bookingFeeChild: 0,
    bookingFeeInfant: 0,
    bookingFeesByAirline: [],
    assistanceCardAdult: 0,
    assistanceCardChild: 0,
    traslado: 0,                // DERIVADO de trasladoItems (no editar directamente)
    trasladoItems: DEFAULT_ROUTE_ITEMS,          // [{ route, active, pricePerPerson, originLabel, destinationLabel }]
    trasladoOperatorId: null,   // UUID del operador seleccionado
    trasladoMode: 'COMPARTIDO', // 'COMPARTIDO' | 'PRIVADO'
    islaSaona: ISLA_SAONA,
    santoDomingo: SANTO_DOMINGO,
    tours: {},
    status: "POR COTIZAR",
    advisorObservations: "",
    placeId: "1",
    tripType: 'internacional',
    destinationIds: [],
    programInclusions: ALL_PREDEFINED_INCLUSIONS,
    programInclusionsItems: DEFAULT_PROGRAM_INCLUSIONS,
    programExclusions: ALL_PREDEFINED_EXCLUSIONS,
    programExclusionsItems: ALL_PREDEFINED_EXCLUSIONS.map((exclusion, idx) => ({
      id: `predefined-${idx}`,
      text: exclusion,
      defaultText: exclusion,
      isPredefined: true,
      checked: true,
    })),
    importantNotes: ALL_IMPORTANT_NOTES,
    importantNotesItems: ALL_IMPORTANT_NOTES.map((note, idx) => ({
      id: `predefined-${idx}`,
      text: note,
      defaultText: note,
      isPredefined: true,
      checked: true,
    })),
  },
  aerolinePrices: [],
  hotelPrices: [],
  aerolinesSelected: [],
  hotelsSelected: [],
  hotelOptions: [],
  pricingMode: 'PER_ROOM',
  accommodationCosts: {},
  aerolineCosts: {},
  flightMode: 'legacy',
}

export const syncMultidestinoLegsInternal = (state) => {
  if (state.cotizationDetail.tripType !== 'multidestino') return;
  
  const originCity = state.cotizationDetail.originCity || 'Lima';
  const destinations = state.cotizationDetail.destinationIds || [];
  
  if (destinations.length === 0) {
    // Clear multidestino legs if no destinations
    const legIds = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup === 'multidestino');
    state.aerolinesSelected = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup !== 'multidestino');
    legIds.forEach(id => delete state.aerolineCosts[id]);
    return;
  }

  // Build route stops: [originCity, dest0.name, dest1.name, ..., originCity]
  const stops = [originCity, ...destinations.map(d => d.name || 'Destino'), originCity];
  const expectedLegs = [];
  for (let i = 0; i < stops.length - 1; i++) {
    expectedLegs.push({
      from: stops[i],
      to: stops[i + 1],
      label: `${stops[i]} → ${stops[i + 1]}`,
      index: i,
      routeKey: `${stops[i]}->${stops[i + 1]}`,
    });
  }

  // Gather existing multidestino legs
  const oldLegIds = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup === 'multidestino');
  const oldLegs = oldLegIds.map(id => ({ id, data: state.aerolineCosts[id] }));

  // Keep non-multidestino selections
  const remainingSelected = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup !== 'multidestino');
  const newLegIds = [];

  expectedLegs.forEach((expected, idx) => {
    // Deterministic ID: leg_${idx}
    const legId = `leg_${idx}`;
    newLegIds.push(legId);

    // Smart reconciliation: match by routeKey first, then by previous leg at same index, or empty
    const matchedOld = oldLegs.find(l => l.data?.routeKey === expected.routeKey)
                    || oldLegs.find(l => l.data?.legIndex === idx)
                    || (state.aerolineCosts[legId] ? { data: state.aerolineCosts[legId] } : null);

    state.aerolineCosts[legId] = {
      ...(matchedOld?.data || {}),
      legIndex: idx,
      legLabel: expected.label,
      legGroup: 'multidestino',
      flightSegmentType: 'combined',
      routeKey: expected.routeKey,
      legOrigin: expected.from,
      legDestination: expected.to,
      totalCost: matchedOld?.data?.totalCost || 0,
      adultCost: matchedOld?.data?.adultCost || 0,
      kidCost: matchedOld?.data?.kidCost || 0,
      infantCost: matchedOld?.data?.infantCost || 0,
      airlineId: matchedOld?.data?.airlineId || null,
      airlineName: matchedOld?.data?.airlineName || '',
      airlineConfigId: matchedOld?.data?.airlineConfigId || null,
      flightTypeName: matchedOld?.data?.flightTypeName || null,
      logoUrl: matchedOld?.data?.logoUrl || null,
      itineraryImageUrl: matchedOld?.data?.itineraryImageUrl || null,
      ocrRawData: matchedOld?.data?.ocrRawData || null,
      segments: matchedOld?.data?.segments || [],
      additionalServices: matchedOld?.data?.additionalServices || [],
    };
  });

  // Clean up old legs that were not reused (e.g. old UUID keys or legs beyond new route length)
  oldLegIds.forEach(oldId => {
    if (!newLegIds.includes(oldId)) {
      delete state.aerolineCosts[oldId];
    }
  });

  state.aerolinesSelected = [...remainingSelected, ...newLegIds];
};

export const cotizationSlice = createSlice({
  name: 'cotization',
  initialState,
  reducers: {
    clean: (state, action) => {
      state.cotizationDetail = {
        id: null,
        originCity: 'Lima',
        originAirport: 'LIM',
        placeId: "0",
        tripType: 'internacional',
        destinationIds: [],
        rooms: [],
        totalAdults: 1,
        totalChildren: 0,
        childrenAges: [],
        days: 0,
        startDate: "",
        endDate: "",
        adultFee: 150,
        kidFee: 100,
        infantFee: 50,
        bookingFeeAdult: 0,
        bookingFeeChild: 0,
        bookingFeeInfant: 0,
        bookingFeesByAirline: [],
        assistanceCardAdult: 0,
        assistanceCardChild: 0,
        traslado: 0,
        trasladoItems: DEFAULT_ROUTE_ITEMS,
        trasladoOperatorId: null,
        trasladoMode: 'COMPARTIDO',
        islaSaona: ISLA_SAONA,
        santoDomingo: SANTO_DOMINGO,
        customer: '',
        tours: {},
        status: "POR COTIZAR",
        advisorObservations: "",
        programInclusions: ALL_PREDEFINED_INCLUSIONS,
        programInclusionsItems: DEFAULT_PROGRAM_INCLUSIONS,
        programExclusions: ALL_PREDEFINED_EXCLUSIONS,
        programExclusionsItems: ALL_PREDEFINED_EXCLUSIONS.map((exclusion, idx) => ({
          id: `predefined-${idx}`,
          text: exclusion,
          defaultText: exclusion,
          isPredefined: true,
          checked: true,
        })),
        importantNotes: ALL_IMPORTANT_NOTES,
        importantNotesItems: ALL_IMPORTANT_NOTES.map((note, idx) => ({
          id: `predefined-${idx}`,
          text: note,
          defaultText: note,
          isPredefined: true,
          checked: true,
        })),
      }
      state.aerolinePrices = []
      state.hotelPrices = []
      state.aerolinesSelected = []
      state.hotelsSelected = []
      state.hotelOptions = []
      state.pricingMode = 'PER_ROOM'
      state.accommodationCosts = {}
      state.aerolineCosts = {}
      state.flightMode = 'legacy'
    },

    cleanTours: (state, action) => {
      state.cotizationDetail.tours = {}
    },

    syncMultidestinoLegs: (state) => {
      syncMultidestinoLegsInternal(state);
    },

    setOriginCity: (state, { payload }) => {
      const { city, airport } = typeof payload === 'object' ? payload : { city: payload, airport: 'LIM' };
      state.cotizationDetail.originCity = city;
      state.cotizationDetail.originAirport = airport || 'LIM';
      if (state.cotizationDetail.tripType === 'multidestino') {
        syncMultidestinoLegsInternal(state);
      }
    },

    setProgramInclusions: (state, { payload }) => {
      state.cotizationDetail.programInclusions = payload;
    },
    setProgramInclusionsItems: (state, { payload }) => {
      state.cotizationDetail.programInclusionsItems = payload;
      state.cotizationDetail.programInclusions = Array.isArray(payload)
        ? payload.filter(item => item && item.checked).map(item => item.title)
        : [];
    },
    setProgramExclusions: (state, { payload }) => {
      state.cotizationDetail.programExclusions = payload;
    },
    setProgramExclusionsItems: (state, { payload }) => {
      state.cotizationDetail.programExclusionsItems = payload;
      state.cotizationDetail.programExclusions = Array.isArray(payload)
        ? payload.filter(item => item && item.checked).map(item => item.text)
        : [];
    },
    setImportantNotes: (state, { payload }) => {
      state.cotizationDetail.importantNotes = payload;
    },
    setImportantNotesItems: (state, { payload }) => {
      state.cotizationDetail.importantNotesItems = payload;
      state.cotizationDetail.importantNotes = Array.isArray(payload)
        ? payload.filter(item => item && item.checked).map(item => item.text)
        : [];
    },
    setAdvisorObservations: (state, { payload }) => {
      state.cotizationDetail.advisorObservations = payload;
    },

    setCotizationDetail: (state, action) => {
      const rooms = action.payload.rooms;
      state.cotizationDetail = {
        ...action.payload,
        rooms: (Array.isArray(rooms) && rooms.length > 0) ? rooms : [initialRoom]
      }
    },

    setRoomsDetail: (state, action) => {
      const rooms = action.payload.map(room => ({
        room_number: room.room_number,
        adults: room.adults,
        children: room.children,
        children_ages: room.children_ages || [],
      }));
      state.cotizationDetail.rooms = rooms;

      let totalAdults = 0;
      let totalChildren = 0;
      let childrenAges = [];
      rooms.forEach(room => {
          totalAdults += room.adults;
          totalChildren += room.children;
          if (room.children_ages) {
              childrenAges = [...childrenAges, ...room.children_ages];
          }
      });
      state.cotizationDetail.totalAdults = totalAdults;
      state.cotizationDetail.totalChildren = totalChildren;
      state.cotizationDetail.childrenAges = childrenAges;
    },

    updateOnAttributeDetail: (state, { payload }) => {
      const { attribute, value } = payload
      state.cotizationDetail[attribute] = value
    },

    // Actualiza el array completo de items de traslado y re-deriva el costo total
    setTrasladoItems: (state, { payload }) => {
      state.cotizationDetail.trasladoItems = payload;
      state.cotizationDetail.traslado = deriveTraslado(payload);
    },

    // Actualiza un campo específico de un item de traslado por su route key
    updateTrasladoItem: (state, { payload }) => {
      const { route, field, value } = payload;
      let items = state.cotizationDetail.trasladoItems;
      if (!Array.isArray(items) || items.length === 0) {
        items = DEFAULT_ROUTE_ITEMS.map(r => ({ ...r }));
      } else {
        items = items.map(i => ({ ...i }));
      }
      let idx = items.findIndex(i => i.route === route);
      if (idx === -1) {
        const defaultItem = DEFAULT_ROUTE_ITEMS.find(r => r.route === route);
        items.push(defaultItem ? { ...defaultItem } : { route, active: false, pricePerPerson: 35, originLabel: '', destinationLabel: '' });
        idx = items.length - 1;
      }
      items[idx][field] = value;
      state.cotizationDetail.trasladoItems = items;
      state.cotizationDetail.traslado = deriveTraslado(items);
    },

    setTrasladoOperatorId: (state, { payload }) => {
      state.cotizationDetail.trasladoOperatorId = payload;
    },

    setTrasladoMode: (state, { payload }) => {
      state.cotizationDetail.trasladoMode = payload;
    },

    updateTour: (state, { payload }) => {
      const { attribute, value } = payload
      state.cotizationDetail.tours[attribute] = value
    },

    setAerolinePrices: (state, action) => {
      state.aerolinePrices = action.payload
    },
    setHotelPrices: (state, action) => {
      state.hotelPrices = action.payload
    },
    updateAerolineOption: (state, { payload }) => {
      state.aerolinePrices = state.aerolinePrices.map(aerolinePrice => aerolinePrice.name === payload.name ? payload : aerolinePrice)
    },
    updateHotelOption: (state, { payload }) => {
      state.hotelPrices = state.hotelPrices.map(hotelPrice => hotelPrice.name === payload.name ? payload : hotelPrice)
    },
    setAerolinesSelected: (state, action) => {
      state.aerolinesSelected = action.payload
    },
    sethotelsSelected: (state, action) => {
      state.hotelsSelected = action.payload
    },
    addHotelOption: (state, { payload }) => {
      state.hotelOptions.push(payload);
    },
    removeHotelOption: (state, { payload: optionId }) => {
      state.hotelOptions = state.hotelOptions.filter(o => o.id !== optionId);
    },
    updateHotelOptionConfig: (state, { payload }) => {
      const option = state.hotelOptions.find(o => o.id === payload.optionId);
      if (option) {
        option.configs[payload.roomIndex] = payload.configId;
      }
    },
    clearHotelOptions: (state) => {
      state.hotelOptions = [];
    },
    setHotelOptions: (state, { payload }) => {
      state.hotelOptions = payload;
    },
    setPricingMode: (state, { payload }) => {
      state.pricingMode = payload;
    },
    updateHotelAccommodationCost: (state, { payload }) => {
      const { hotelId, objectCost } = payload
      state.accommodationCosts[hotelId] = objectCost
    },
    updateAerolineCost: (state, { payload }) => {
      const { aerolineId, objectCost } = payload
      // Preserve the existing flightSegmentType and additionalServices when updating costs
      const existing = state.aerolineCosts[aerolineId] || {}
      state.aerolineCosts[aerolineId] = {
        ...existing,
        ...objectCost,
      }
    },
    updateAerolineItinerary: (state, { payload }) => {
      const { aerolineId, itineraryImageUrl, ocrRawData, segments } = payload
      const existing = state.aerolineCosts[aerolineId] || {}
      state.aerolineCosts[aerolineId] = {
        ...existing,
        itineraryImageUrl: itineraryImageUrl !== undefined ? itineraryImageUrl : existing.itineraryImageUrl,
        ocrRawData: ocrRawData !== undefined ? ocrRawData : existing.ocrRawData,
        segments: segments !== undefined ? segments : existing.segments,
      }
    },
    updateAerolineAdditionalServices: (state, { payload }) => {
      const { aerolineId, additionalServices } = payload
      const existing = state.aerolineCosts[aerolineId] || {}
      state.aerolineCosts[aerolineId] = {
        ...existing,
        additionalServices,
      }
    },
    // Changes the flight segment type for a specific airline card.
    // 'international_domestic' means two separate prices (intl + domestic),
    // 'international' means international only, 'combined' means full trip in one price.
    updateAerolineFlightSegment: (state, { payload }) => {
      const { aerolineId, flightSegmentType } = payload
      const existing = state.aerolineCosts[aerolineId] || {}
      state.aerolineCosts[aerolineId] = {
        ...existing,
        flightSegmentType,
        // Clear price data when switching modes to avoid stale values
        totalCost: 0,
        adultCost: 0,
        kidCost: 0,
        infantCost: 0,
        domesticTotalCost: 0,
        domesticAdultCost: 0,
        domesticKidCost: 0,
        domesticInfantCost: 0,
      }
    },
    clearAccommodationCosts: (state) => {
      state.accommodationCosts = {}
    },
    clearAerolineCosts: (state) => {
      state.aerolineCosts = {}
    },
    setFlightMode: (state, { payload }) => {
      state.flightMode = payload;
    },
    addFlightLeg: (state, { payload }) => {
      const { legId, legIndex, legLabel, legGroup, flightSegmentType, legOrigin, legDestination } = payload;
      if (!state.aerolinesSelected.includes(legId)) {
        state.aerolinesSelected.push(legId);
      }
      state.aerolineCosts[legId] = {
        legIndex,
        legLabel,
        legGroup,
        flightSegmentType: flightSegmentType || 'combined',
        legOrigin: legOrigin || null,
        legDestination: legDestination || null,
        totalCost: 0,
        adultCost: 0,
        kidCost: 0,
        infantCost: 0,
        airlineId: null,
        airlineName: '',
        airlineConfigId: null,
        itineraryImageUrl: null,
        ocrRawData: null,
        segments: [],
        additionalServices: [],
      };
    },
    removeFlightLeg: (state, { payload: legId }) => {
      state.aerolinesSelected = state.aerolinesSelected.filter(id => id !== legId);
      delete state.aerolineCosts[legId];
      // Reindex remaining legs that have legGroup
      const remainingLegs = state.aerolinesSelected
        .filter(id => state.aerolineCosts[id]?.legGroup)
        .sort((a, b) => (state.aerolineCosts[a]?.legIndex || 0) - (state.aerolineCosts[b]?.legIndex || 0));
      remainingLegs.forEach((id, idx) => {
        if (state.aerolineCosts[id]) {
          state.aerolineCosts[id].legIndex = idx;
          if (state.aerolineCosts[id].legGroup === 'multidestino' && !state.aerolineCosts[id].legLabel?.includes('→')) {
            state.aerolineCosts[id].legLabel = `Tramo ${idx + 1}`;
          }
        }
      });
    },
    updateFlightLegAirline: (state, { payload }) => {
      const { legId, airlineId, airlineName, airlineConfigId, flightTypeName, logoUrl } = payload;
      const existing = state.aerolineCosts[legId] || {};
      state.aerolineCosts[legId] = {
        ...existing,
        airlineId,
        airlineName,
        airlineConfigId,
        flightTypeName,
        logoUrl,
      };
    },
    clearFlightLegs: (state) => {
      const legIds = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup);
      state.aerolinesSelected = state.aerolinesSelected.filter(id => !state.aerolineCosts[id]?.legGroup);
      legIds.forEach(id => {
        delete state.aerolineCosts[id];
      });
    },
    setTripType: (state, { payload }) => {
      const oldType = state.cotizationDetail.tripType;
      state.cotizationDetail.tripType = payload;
      if (payload !== oldType) {
        if (payload !== 'multidestino') {
          state.cotizationDetail.destinationIds = [];
        }
        // Clear flight legs & selections of legGroup
        const legIds = state.aerolinesSelected.filter(id => state.aerolineCosts[id]?.legGroup);
        state.aerolinesSelected = state.aerolinesSelected.filter(id => !state.aerolineCosts[id]?.legGroup);
        legIds.forEach(id => { delete state.aerolineCosts[id]; });

        if (payload === 'multidestino') {
          state.flightMode = 'multidestino';
          syncMultidestinoLegsInternal(state);
        } else if (payload === 'nacional') {
          state.flightMode = 'combined';
        } else {
          state.flightMode = 'international';
        }
      }
    },
    addDestination: (state, { payload }) => {
      const { placeId, name, country } = payload;
      if (!state.cotizationDetail.destinationIds) {
        state.cotizationDetail.destinationIds = [];
      }
      if (state.cotizationDetail.destinationIds.some(d => String(d.placeId) === String(placeId))) {
        return;
      }
      state.cotizationDetail.destinationIds.push({
        placeId,
        name,
        country,
        order: state.cotizationDetail.destinationIds.length,
      });
      // Sincronizar el primer destino como placeId principal para compatibilidad
      if (state.cotizationDetail.destinationIds.length === 1) {
        state.cotizationDetail.placeId = String(placeId);
      }
      syncMultidestinoLegsInternal(state);
    },
    removeDestination: (state, { payload: placeId }) => {
      if (!Array.isArray(state.cotizationDetail.destinationIds)) return;
      state.cotizationDetail.destinationIds = state.cotizationDetail.destinationIds
        .filter(d => String(d.placeId) !== String(placeId))
        .map((d, idx) => ({ ...d, order: idx }));
      if (state.cotizationDetail.destinationIds.length > 0) {
        state.cotizationDetail.placeId = String(state.cotizationDetail.destinationIds[0].placeId);
      } else {
        state.cotizationDetail.placeId = "0";
      }

      // Cleanup related selections
      // 1. Remove hotels
      state.hotelsSelected = state.hotelsSelected.filter(id => {
         const option = state.hotelOptions.find(o => o.id === id);
         return !(option && String(option.destinationId || option.placeId) === String(placeId));
      });
      // 2. Remove tours
      if (state.cotizationDetail.tours) {
        if (Array.isArray(state.cotizationDetail.tours)) {
          state.cotizationDetail.tours = state.cotizationDetail.tours.filter(
            t => t && String(t.placeId || t.destinationId) !== String(placeId)
          );
        } else if (typeof state.cotizationDetail.tours === 'object') {
          Object.keys(state.cotizationDetail.tours).forEach(tourKey => {
            const t = state.cotizationDetail.tours[tourKey];
            if (t && String(t.placeId || t.destinationId) === String(placeId)) {
              delete state.cotizationDetail.tours[tourKey];
            }
          });
        }
      }
      syncMultidestinoLegsInternal(state);
    },
    reorderDestinations: (state, { payload: orderedPlaceIds }) => {
      if (!Array.isArray(state.cotizationDetail.destinationIds)) return;
      const current = state.cotizationDetail.destinationIds;
      const reordered = orderedPlaceIds
        .map((pid, idx) => {
          const existing = current.find(d => String(d.placeId) === String(pid));
          return existing ? { ...existing, order: idx } : null;
        })
        .filter(Boolean);
      state.cotizationDetail.destinationIds = reordered;
      if (reordered.length > 0) {
        state.cotizationDetail.placeId = String(reordered[0].placeId);
      }
      syncMultidestinoLegsInternal(state);
    },
  },
  extraReducers: (builder) => {
  }
})

export const activeDestinationIdsSelector = (state) => {
  const detail = state?.cotization?.cotizationDetail;
  if (!detail) return [];
  if (detail.tripType === 'multidestino' && Array.isArray(detail.destinationIds) && detail.destinationIds.length > 0) {
    return detail.destinationIds.map(d => Number(d.placeId)).filter(Boolean);
  }
  return detail.placeId && detail.placeId !== "0" ? [Number(detail.placeId)] : [];
};

export const {
  clean,
  setCotizationDetail,
  setAerolinePrices,
  setHotelPrices,
  updateAerolineOption,
  updateHotelOption,
  updateOnAttributeDetail,
  sethotelsSelected,
  setAerolinesSelected,
  addHotelOption,
  removeHotelOption,
  updateHotelOptionConfig,
  clearHotelOptions,
  setHotelOptions,
  setPricingMode,
  updateTour,
  cleanTours,
  setRoomsDetail,
  updateHotelAccommodationCost,
  updateAerolineCost,
  updateAerolineItinerary,
  updateAerolineAdditionalServices,
  updateAerolineFlightSegment,
  clearAccommodationCosts,
  clearAerolineCosts,
  setFlightMode,
  addFlightLeg,
  removeFlightLeg,
  updateFlightLegAirline,
  clearFlightLegs,
  setTripType,
  addDestination,
  removeDestination,
  reorderDestinations,
  setTrasladoItems,
  updateTrasladoItem,
  setTrasladoOperatorId,
  setTrasladoMode,
  setProgramInclusions,
  setProgramInclusionsItems,
  setProgramExclusions,
  setProgramExclusionsItems,
  setImportantNotes,
  setImportantNotesItems,
  setAdvisorObservations,
  setOriginCity,
} = cotizationSlice.actions;

export default cotizationSlice.reducer;